import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { Button } from "@/components/ui/button";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/admin/AsyncView";

const RANGES = [7, 30, 90];
const AXIS = "#8E8E93";
const GRID = "rgba(255,255,255,0.07)";
const TIP = { background: "#232326", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, color: "#FAFAFA" };
const HUE = "#007AFF";

// Laporan: rentang 7/30/90 hari di URL, satuan dan cakupan jujur
// ("jenis alat", bukan unit), satu hue data, export mandiri per tombol.
export default function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const days = [7, 30, 90].includes(Number(params.get("days"))) ? Number(params.get("days")) : 30;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/admin/reports", { params: { days } });
      setReport(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting("csv");
    try {
      const { data } = await api.get("/admin/reports/export", { params: { days }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `laporan-${days}hari.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export CSV gagal: ${errMsg(e)}`);
    } finally {
      setExporting(null);
    }
  };

  const exportPdf = async () => {
    if (!report) return;
    setExporting("pdf");
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      let y = 20;
      const line = (t, size = 11, bold = false) => {
        doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.text(t, 15, y); y += size < 13 ? 7 : 10;
      };
      line(`LAPORAN PEMINJAMAN ${days} HARI`, 16, true);
      line(`Total booking: ${report.totals.bookings} - Selesai: ${report.totals.returned} - Terlambat: ${report.totals.overdue}`);
      line(`Acara tanpa barang: ${report.totals.location_only ?? 0} - Unit alat: ${report.totals.equipment_units ?? 0}`);
      line(`Durasi terjadwal bersama: ${report.totals.scheduled_hours ?? 0} jam`);
      y += 4;
      line("BARANG PALING SERING DIPINJAM", 13, true);
      report.top_items.slice(0, 6).forEach((item, n) => line(`${n + 1}. ${item.name} - ${item.count} kali`));
      y += 4;
      line("PEMINJAM TERAKTIF", 13, true);
      report.top_users.forEach((u, n) => line(`${n + 1}. ${u.name} - ${u.count} kali`));
      y += 4;
      line("SERING TERLAMBAT", 13, true);
      (report.late_users.length ? report.late_users : [{ name: "Tidak ada", count: 0 }])
        .forEach((u, n) => line(`${n + 1}. ${u.name} - ${u.count} kali`));
      doc.save(`laporan-${days}hari.pdf`);
    } catch (e) {
      toast.error(`Export PDF gagal: ${errMsg(e)}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-white">Laporan</h1>
        <p className="text-sm text-zinc-400">Aktivitas {days} hari terakhir, dihitung dari data booking.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Rentang laporan" className="flex gap-1.5">
          {RANGES.map((d) => (
            <button key={d} data-testid={`report-range-${d}`}
              onClick={() => setParams({ days: String(d) }, { replace: true })}
              aria-pressed={days === d}
              className={`h-10 min-h-[44px] rounded-full border px-4 text-sm transition-colors ${
                days === d ? "border-white/25 bg-white/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"
              }`}>
              {d} hari
            </button>
          ))}
        </div>
        <span className="flex-1" />
        <Button data-testid="export-csv-button" variant="outline" disabled={exporting === "csv" || !report} onClick={exportCsv}
          className="h-10 rounded-full border-white/15 bg-transparent px-4 text-sm text-zinc-200 hover:bg-white/10 disabled:opacity-50">
          {exporting === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export CSV
        </Button>
        <Button data-testid="export-pdf-button" disabled={exporting === "pdf" || !report} onClick={exportPdf}
          className="h-10 rounded-full bg-[#0069DB] px-4 text-sm font-semibold text-white hover:bg-[#0058B6] disabled:opacity-50">
          {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export PDF
        </Button>
      </div>

      {loading && <ListSkeleton rows={5} />}
      {!loading && error && <ErrorState message={error} onRetry={load} />}

      {!loading && !error && report && (
        <>
          <section aria-label="Ringkasan jenis booking" className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg border border-white/10 p-4 text-sm"><p>Acara tanpa barang <strong>{report.totals.location_only ?? 0}</strong></p><p>Unit alat <strong>{report.totals.equipment_units ?? 0}</strong></p><p>Durasi bersama <strong>{report.totals.scheduled_hours ?? 0} jam</strong></p></section>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Total booking" value={report.totals.bookings} testId="report-total-bookings" />
            <Stat label="Selesai" value={report.totals.returned} testId="report-returned" />
            <Stat label="Terlambat" value={report.totals.overdue} testId="report-overdue" />
            <Stat label="Jenis alat terkatalog" value={report.totals.items} testId="report-items" />
          </div>

          <section aria-label="Penggunaan lokasi" className="rounded-lg border border-white/10 p-4"><h2 className="font-semibold">Penggunaan lokasi</h2>{(report.top_locations || []).map((l,i) => <p key={l.name || i} className="mt-2 text-sm text-zinc-400">{l.name || l.location || "Legacy"}: {l.count} booking</p>)}{!report.top_locations?.length && <p className="mt-2 text-sm text-zinc-500">Belum ada penggunaan lokasi tercatat.</p>}</section>
          <section aria-label="Tren peminjaman" data-testid="report-trend-chart" className="rounded-2xl border border-white/10 bg-zinc-900 p-4 sm:p-5">
            <h2 className="text-xs font-medium text-zinc-500">Tren peminjaman - booking per hari</h2>
            {report.trend.length === 0 ? (
              <p className="py-8 text-center text-sm text-zinc-500">Belum ada data pada rentang ini.</p>
            ) : (
              <div className="mt-4 h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={report.trend} margin={{ left: -12, right: 8 }}>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="date" stroke={AXIS} fontSize={11} tickMargin={8} minTickGap={24} />
                    <YAxis stroke={AXIS} fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={TIP} />
                    <Line type="monotone" dataKey="count" name="Booking" stroke={HUE} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <section aria-label="Barang paling sering dipinjam, 6 teratas" data-testid="report-top-items" className="rounded-2xl border border-white/10 bg-zinc-900 p-4 sm:p-5">
              <h2 className="text-xs font-medium text-zinc-500">Barang paling sering dipinjam - 6 teratas, total unit-booking</h2>
              {report.top_items.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-500">Belum ada data.</p>
              ) : (
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.top_items.slice(0, 6)} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid stroke={GRID} horizontal={false} />
                      <XAxis type="number" stroke={AXIS} fontSize={11} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" stroke={AXIS} fontSize={11} width={130} />
                      <Tooltip contentStyle={TIP} />
                      <Bar dataKey="count" name="Kali dipinjam" fill={HUE} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

            <div className="space-y-4">
              <section aria-label="Peminjam teraktif" data-testid="report-top-users" className="rounded-2xl border border-white/10 bg-zinc-900 p-4 sm:p-5">
                <h2 className="text-xs font-medium text-zinc-500">Peminjam teraktif - jumlah booking</h2>
                <div className="mt-3 space-y-1.5">
                  {report.top_users.map((u) => (
                    <div key={u.name} className="flex items-center justify-between rounded-xl bg-zinc-950/60 px-4 py-2.5 text-sm">
                      <span className="min-w-0 truncate text-zinc-200">{u.name}</span>
                      <span className="ml-3 shrink-0 font-mono text-xs tabular-nums text-zinc-400">{u.count} kali</span>
                    </div>
                  ))}
                  {report.top_users.length === 0 && <EmptyState kind="first" title="Belum ada data." hint="" />}
                </div>
              </section>
              <section aria-label="Sering terlambat" data-testid="report-late-users" className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-4 sm:p-5">
                <h2 className="text-xs font-medium text-rose-300">Sering terlambat - jumlah keterlambatan</h2>
                <div className="mt-3 space-y-1.5">
                  {report.late_users.map((u) => (
                    <div key={u.name} className="flex items-center justify-between rounded-xl bg-zinc-950/40 px-4 py-2.5 text-sm">
                      <span className="min-w-0 truncate text-zinc-200">{u.name}</span>
                      <span className="ml-3 shrink-0 font-mono text-xs tabular-nums text-rose-300">{u.count} kali</span>
                    </div>
                  ))}
                  {report.late_users.length === 0 && <p className="py-2 text-sm text-zinc-500">Tidak ada keterlambatan.</p>}
                </div>
              </section>
            </div>
          </div>

          <p className="text-xs text-zinc-600">Cakupan: booking yang dibuat dalam {days} hari terakhir; total dan peringkat dihitung server untuk seluruh cakupan itu.</p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/10 bg-zinc-900 p-4 sm:p-5">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="mt-1.5 font-mono text-2xl font-semibold tabular-nums text-zinc-100">{value}</p>
    </div>
  );
}
